import {
  DIALOG_DATA,
  DIALOG_REF,
  DialogService,
} from '@ansys-internal/awf-angular/shared';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
} from '@ansys-internal/awc-angular/buttons';
import { FlexLayout } from '@ansys-internal/awc-angular/core';
import { InputType, SelectComponent } from '@ansys-internal/awc-angular/forms';
import {
  IconType,
  Icons,
  IconComponent,
} from '@ansys-internal/awc-angular/icons';
import { AWCListItem } from '@ansys-internal/awc-angular/lists';
import { ConceptSections } from 'src/app/shared/enums/concept.enum';
import { AddPartDialogData } from 'src/app/shared/types';
import { WizardProgress, PartType } from 'src/app/shared/enums';
import { ConceptPartType } from 'src/app/shared/types';
import { selectItemMap } from './lib/select-item-map';
import { defaultPartsMap } from './lib/default-parts-map';
import { ImportLibraryAssetComponent } from '../import-library-asset/import-library-asset.component';
import { ComponentFileType, SolvedInverter } from '../../../../api';
import { UploadFileResponseType } from '../../../shared/types/upload-file-response.type';
import { PartInputsComponent } from '../../widgets/part-inputs/part-inputs.component';
import { CommonModule } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import { State } from '@ansys-internal/awf-angular/store';
import { FeatureFlagsState } from '../../../state/lib/feature-flags.state';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagsEnum } from '../../../shared/enums/feature-flags.enum';
import {ComponentsService} from "../../../shared/services/components.service";

@Component({
    selector: 'app-add-part',
    templateUrl: './add-part.component.html',
    styleUrls: ['./add-part.component.scss'],
    imports: [
        IconComponent,
        ButtonComponent,
        PartInputsComponent,
        SelectComponent,
        CommonModule,
    ]
})
export class AddPartComponent {
  @ViewChild('fileUpload') upload!: ElementRef;
  public step: WizardProgress = WizardProgress.START;
  protected readonly steps = WizardProgress;
  protected readonly partTypes = PartType;
  protected type: ButtonType = ButtonType.PRIMARY;
  protected alttype: ButtonType = ButtonType.SECONDARY;
  protected size: ButtonSize = ButtonSize.MEDIUM;
  protected smallsize: ButtonSize = ButtonSize.SMALL;
  protected suffixIcon!: IconType;
  protected libraryIcon: IconType = {
    src: 'https://s2.svgbox.net/octicons.svg?ic=book',
  };
  protected label: string = '';
  protected placeholder: string = 'Select an option...';
  protected items: AWCListItem[] = [];
  protected iconButton: Icons = Icons.ADD_GEOMETRY;
  protected spinner: Icons = Icons.SPINNER;
  protected layout: FlexLayout = FlexLayout.COLUMN;
  protected addIcon: IconType = { icon: Icons.ADD };
  protected spinnerIconType: IconType = { icon: Icons.SPINNER };
  protected checkIconType: IconType = { icon: Icons.CHECK };
  protected selectedItems: string[] = [];
  protected loading: boolean = false;
  protected uploadedFile?: File;
  protected defaultPart: any = { name: '' };
  protected numberType: InputType = InputType.NUMBER;
  protected fileTypeAcceptance: string = '';
  protected selectedItem: AWCListItem | undefined;
  protected disableDropdown = false;
  protected uploadingFilePart: boolean = false;
  protected uploadedFilePart?: UploadFileResponseType;
  protected thermalFile!: UploadFileResponseType;
  protected partsLoaded: boolean = true;
  private userConfirmed: boolean = false;
  private selectItemMap: Map<string, AWCListItem[]> = selectItemMap;
  private destroy$ = new Subject<void>();
  protected readonly FeatureFlagsEnum = FeatureFlagsEnum;
  private defaultParts: Map<PartType, ConceptPartType> = defaultPartsMap as Map<
    PartType,
    ConceptPartType
  >;
  private uploading$ = this.componentService.uploading$;

  constructor(
    @Inject(DIALOG_DATA) public data: AddPartDialogData,
    @Inject(DIALOG_REF) public dialogRef: DialogRef,
    private dialogService: DialogService,
    private _cdr: ChangeDetectorRef,
    private state: State,
    private componentService: ComponentsService
  ) {
    this.suffixIcon = this.data.parent.prefixIcon as IconType;
    this.stepWizard(); // Skipping step 1 till such a time when we get library filtering for the 'Import from Library' option
  }

  ngOnInit(): void {
    this.uploading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: boolean) => {
        this.uploadingFilePart = state;
      });

    this.items = this.selectItemMap.get(this.data.parent.id) as AWCListItem[];

    if (this.data.parent.id === SolvedInverter.solved_component_type.INVERTER) {
      this.state
        .get(FeatureFlagsState)
        .pipe(takeUntil(this.destroy$))
        .subscribe((flags: any) => {
          if (flags && !flags[FeatureFlagsEnum.FEATURE_INVERTER_LOSS_MAP]) {
            this.items = this.items.filter(
              (item) => item.id !== 'InverterLossMapID'
            );
          }
        });
    }

    if (this.items?.length > 0) {
      this.selectedItems = [this.items[0].id];
    }

    if (this.data.step) {
      const { type, parent, step } = this.data;

      if (
        (type === ConceptSections.CONFIGURATIONS ||
          type === ConceptSections.REQUIREMENT ||
          parent.id === 'clutch') &&
        step === WizardProgress.MIDDLE
      ) {
        this.step = WizardProgress.END;
      } else {
        this.step = step;
      }
    }

    if (this.items?.length) {
      this.selectedItem = this.items[0];
      this.getFileTypeFromSelectedItem();
    }

    this.defaultPart = this.defaultParts.get(
      this.data.parent.id as PartType
    ) || { name: '' };

    if (this.items[0]) {
      this.selectionChanged([this.items[0]]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected iconButtonAction(): void {
    if (this.selectedItem?.userData && this.selectedItem.userData['upload']) {
      // Prevent the dropdown from opening
      this.disableDropdown = true;
      setTimeout(() => {
        this.disableDropdown = false;
      });
      this.updateDefaultPartsWithSelection(); // Ensure defaultPart is updated before upload

      this.upload.nativeElement.click();
    } else {
      this.step = WizardProgress.END;
    }
  }

  protected selectionChanged($event: AWCListItem[]): void {
    this.selectedItems = [$event[0].id];
    this.selectedItem = $event[0];

    this.updateDefaultPartsWithSelection();
    this.getFileTypeFromSelectedItem();
  }

  private updateDefaultPartsWithSelection(): void {
    if (this.selectedItem?.userData && this.selectedItem.userData['upload']) {
      const refId =
        (this.data.parent.id as PartType) === PartType.MOTOR &&
        this.selectedItem.userData['type'] !== 'motor_torque_grid_file'
          ? this.selectedItem.userData['type']
          : this.data.parent.id;
      this.defaultPart = this.selectedItem.userData['defaultPart'] ??
        this.defaultParts.get(refId as PartType) ?? { name: '' };
    } else {
      if (this.data.parent.id === 'inverter') {
        this.defaultPart.inverter_data = this.selectedItem?.userData;
      }
    }
  }

  protected stepWizard(): void {
    if (
      this.data.type === ConceptSections.CONFIGURATIONS ||
      this.data.type === ConceptSections.REQUIREMENT ||
      this.data.parent.id === 'clutch'
    ) {
      this.step = WizardProgress.END;
    } else {
      this.step++;
    }
  }

  protected clickedLibrary(): void {
    // TODO: Wait for filtering to become available in awf-table to filter by `type`
    this.dialogService.open(ImportLibraryAssetComponent, {
      title: 'Library',
      data: { type: this.data.parent.id },
    });
    this.dialogRef.close();
  }

  protected async importFile($event: Event): Promise<void> {
    if (this.uploadingFilePart) {
      return;
    }

    const input = $event.target as HTMLInputElement;
    if (!input?.files?.length) return;
    const file: File = input.files[0];
    if (file) {
      this.uploadedFile = file;
      this.defaultPart = {
        ...this.defaultPart,
        name: file.name.split('.')[0],
      };
      this.step = WizardProgress.END;

      // Start upload process early
      if (this.data.onUploadFilePart) {
        this.uploadingFilePart = true;
        try {
          this.uploadedFilePart = await this.data.onUploadFilePart(
            this.uploadedFile,
            this.selectedItem?.userData
              ? (this.selectedItem.userData['type'] as ComponentFileType)
              : ('' as ComponentFileType)
          );
        } catch (e) {
          this.uploadingFilePart = false;
          this.uploadedFile = undefined;
          this.defaultPart = {
            ...this.defaultPart,
            name: '',
          };
          this.step = WizardProgress.MIDDLE;
        }

        // If by the time the file has uploaded, the user has submitted the next stage, action it here
        if (this.userConfirmed) {
          this.dataConfirm();
        }
        this.uploadingFilePart = false;
      }
    }

    this._cdr.detectChanges();
  }

  protected uploadClick(): void {
    this.upload.nativeElement.value = '';
  }

  protected confirm(): void {
    this.loading = true;
    if (this.uploadingFilePart) {
      // File part is still uploading. Handle data.onConfirm in importFile() method
      this.userConfirmed = true;
    } else {
      this.dataConfirm();
    }
  }

  private dataConfirm(): void {
    this.data
      .onConfirm(
        this.defaultPart,
        this.uploadedFile,
        this.selectedItem?.userData
          ? (this.selectedItem.userData['type'] as string)
          : '',
        this.uploadedFilePart,
        this.thermalFile
      )
      .catch((e) => {
        console.error(e);
        this.loading = false;
        this._cdr.detectChanges();
      });
  }

  private getFileTypeFromSelectedItem(): void {
    this.fileTypeAcceptance = (
      this.selectedItem?.userData?.['file_types'] as string[]
    )?.join(', ');
  }
}
